"use client";

/**
 * RunStep — invokes the resolved action.
 *
 * Two modes (declared in the manifest):
 * - **server**: POST collected payload to /api/actions/resolver/run.
 * - **client-redirect**: Call `manifest.clientRun(resolved, hooks)` in-browser.
 */

import { useEffect, useState } from "react";

import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";

import { getActionManifest } from "@/lib/actions/manifests";
import type { ClientRunHooks, ClientRunResolved } from "@/lib/actions/manifests/types";
import { resolveTerminalTargetIds } from "@/lib/actions/resolution/resolveTargetIds";
import type {
  CollectedValues,
  PrefetchedContact,
} from "@/lib/actions/resolution/types";

interface Props {
  collected: CollectedValues;
  prefetchedTargets?: PrefetchedContact[];
  onDone: (ok: boolean, message?: string) => void;
}

function buildClientHooks(deps: {
  openModal: (id: string, data?: Record<string, unknown>) => void;
  closeModal: (id: string) => void;
}): ClientRunHooks {
  return {
    openModal: (id, data) => deps.openModal(id, data),
    closeModal: (id) => deps.closeModal(id),
    onNavigate: (href) => {
      if (href.startsWith("tel:") || href.startsWith("mailto:")) {
        window.location.href = href;
        return;
      }
      window.location.assign(href);
    },
    onToast: (message, type) => {
      switch (type) {
        case "success":
          toast.success(message);
          break;
        case "error":
          toast.error(message);
          break;
        case "warning":
          toast.warning(message);
          break;
        default:
          toast(message);
      }
    },
  };
}

export function RunStep({ collected, prefetchedTargets, onDone }: Props) {
  const { openModal, closeModal } = useGlobalModalStack();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const manifest = getActionManifest(collected.actionId);
        if (!manifest) {
          onDone(false, "No manifest registered");
          return;
        }

        const mode = manifest.mode ?? "server";

        if (mode === "client-redirect") {
          if (!manifest.clientRun) {
            onDone(false, "Manifest missing clientRun");
            return;
          }
          const targetIds = resolveTerminalTargetIds(collected);
          const resolved: ClientRunResolved = {
            actionId: collected.actionId,
            targetEntity: manifest.requires.entity,
            targetIds,
            fields: collected.fields,
            extras: collected.extras,
            sourceEntity: collected.sourceEntity,
            sourceId: collected.sourceId,
            prefetchedTargets,
          };
          await manifest.clientRun(
            resolved,
            buildClientHooks({ openModal, closeModal }),
          );
          if (!cancelled) onDone(true);
          return;
        }

        const res = await fetch("/api/actions/resolver/run", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collected }),
        });
        const body = (await res.json()) as { ok: boolean; message?: string };
        if (!cancelled) onDone(body.ok, body.message);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Run failed";
        setError(message);
        onDone(false, message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="text-sm text-destructive" role="alert">
        {error}
      </div>
    );
  }
  return (
    <div className="text-sm text-muted-foreground" role="status">
      Working…
    </div>
  );
}
